import { useLocation } from 'react-router'
import { useState, useEffect, useContext } from 'react'
import { ROUTES } from '@/config/routes'
import { ReactSVG } from 'react-svg'
import { Button } from '@/components/Button'
import { Bars3Icon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/20/solid'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Description } from '@headlessui/react'
import { DealerContext } from '@/context/DealerContext'
import PersistedLink from './PersistedLink'
import useFormContext from '@/context/useFormContext'
import { purchasePersonalItems } from '@/data/selectOptions'
import { useNavigationValidation } from '@/hooks/useNavigationValidation'
import usePersistedNavigation from '@/hooks/usePersistedNavigation'
import { useFormSaveForLater } from '@/hooks/useFormSaveForLater'
import { apiPost } from '@/services/apiService'

interface SaveApplicationModalProps {
  open: boolean
  setOpen: (open: boolean) => void
  saveUrl?: string
}

function SaveApplicationModal({ open, setOpen, saveUrl }: SaveApplicationModalProps) {
  const { state } = useFormContext()
  const [copied, setCopied] = useState(false)
  const [isSendingEmail, setIsSendingEmail] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const handleCopy = () => {
    if (saveUrl) {
      navigator.clipboard.writeText(saveUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 5000)
    }
  }

  const handleSendEmail = async () => {
    try {
      setIsSendingEmail(true)
      const currentParams = new URLSearchParams(window.location.search || '')
      const uniqueIdentifier = currentParams.get('uid')
      const dealerId = currentParams.get('dealerid')
      let url = `Form/SendApplicationLinkEmail?id=${state.formId}`
      if (uniqueIdentifier) {
        url += `&uniqueIdentifier=${uniqueIdentifier}`
      }
      if (dealerId) {
        url += `&dealerid=${dealerId}`
      }
      await apiPost(url, {})
      setEmailSent(true)
    } catch (error) {
      console.error('Failed to send email:', error)
      // You might want to show an error message to the user here
    } finally {
      setIsSendingEmail(false)
    }
  }

  // Reset email sent state when modal is closed
  useEffect(() => {
    if (!open) {
      setEmailSent(false)
    }
  }, [open])

  return (
    <Dialog open={open} onClose={() => setOpen(false)} className='Header-item relative z-30'>
      <DialogBackdrop
        transition
        className='fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in'
      />

      <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
        <div className='flex min-h-full items-end justify-center py-4 text-center sm:items-center sm:p-0'>
          <DialogPanel
            transition
            className='relative transform rounded-lg bg-white pt-5 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-xl sm:py-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95'
          >
            <div>
              <div className='p-4 sm:p-6 md:py-8'>
                <div className='flex flex-col gap-4 items-center justify-center'>
                  <div className='max-w-xs mx-auto text-center space-y-5'>
                  <button onClick={() => setOpen(false)} className='cursor-pointer transition duration-300 flex items-center justify-center rounded-full absolute z-10 top-2 right-2 p-2 hover:bg-[var(--theme-bg)] hover:text-[var(--brand-1)]'>
                    <XMarkIcon className='w-7 h-7' />
                  </button>
                    <DialogTitle as='h3' className='text-[28px] text-center mt-3 py-2'>
                      We saved it {state.personal.aboutYou.firstName}
                    </DialogTitle>
                    <Description className='text-lg'>
                      We've saved the details you've already provided for your quote, so you can pick up from where you left off.<br/><br />
                      Your details will be saved for <span className='font-bold'>30 days</span>.
                    </Description>
                    <div className='flex flex-col items-center gap-4 text-lg'>
                      <div className='w-full max-w-xs'>
                        <p className='text-sm uppercase font-[var(--font-weight-bold)] pb-1'>Share your unique url</p>
                        <div className='flex items-center relative bg-[var(--theme-bg)] rounded-[4px] px-2.5 gap-1 w-full'>
                          <input className='grow bg-[var(--theme-bg)] py-3 rounded-[4px] outline-0' readOnly value={saveUrl || ''} />
                          <Button 
                            icon='none' 
                            className='muted-text-button [&.muted-text-button]:px-3' 
                            style='text'
                            onClick={handleCopy}
                          >
                            {copied ? 'Copied!' : 'Copy'}
                          </Button>
                        </div>
                      </div>
                      <span>OR</span>
                      {emailSent ? (
                        <div className='flex justify-center items-center gap-1'>
                        <span className="text-[var(--brand-2)] text-md font-[var(--font-weight-bold)] leading-[1]">Email Sent!</span>
                          <CheckCircleIcon className='size-6 text-[var(--const-success)]' aria-hidden='true' />
                        </div>
                      ) : (
                        <Button 
                          icon='mail' 
                          onClick={handleSendEmail}
                          disabled={isSendingEmail}
                        >
                          {isSendingEmail ? 'Sending...' : 'Send to your email'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  )
}

const Header = () => {
  // @Adam : TODO we need to add a class 'text-[var(--nav-text-hover)]' if the user has already completed the form for the section (Ryan wants a step completion/active/uncompleted aproach)
  const navLinkClasses = `relative inline-flex items-center max-lg:h-auto h-full text-[var(--nav-text)] [&.active]:font-[var(--font-weight-bold)] [&.active]:text-[var(--nav-text-hover)] hover:text-[var(--nav-text-hover)]
   text-center text-base transition duration-300 laeding-[1] max-lg:border-b max-lg:border-b-4 border-transparent max-lg:[&.active]:border-[var(--nav-text-hover)] pt-1 max-xl:py-6 max-xl:px-8
   [&.active]:before:content-[""] [&.active]:before:block [&.active]:before:absolute [&.active]:before:left-0 [&.active]:before:bottom-0
   [&.active]:before:w-full [&.active]:before:h-1 [&.active]:before:rounded-sm [&.active]:before:bg-[var(--nav-text-hover)] max-lg:[&.active]:before:hidden`
  const navLinkIndicatorClasses = 'inline-flex items-center h-full font-[var(--font-weight-bold)] text-[var(--nav-text)] text-[var(--nav-text-hover)] hover:text-[var(--nav-text-hover)] text-center text-sm sm:text-base transition duration-300 laeding-[1] border-b-3 border-[var(--nav-text-hover)]'
  const [mobileMenu, setMobileMenu] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [saveUrl, setSaveUrl] = useState<string>()
  const location = useLocation()
  const navigate = usePersistedNavigation()
  const dealerContext = useContext(DealerContext)
  const { state } = useFormContext()
  const purchaseItems = state.personal.aboutYou.purchaseItems
  const hasPurchaseItems = purchaseItems?.itemType && purchaseItems?.purchasePrice && purchaseItems?.loanTerm && purchaseItems?.repaymentFrequency
  const { validateNavigationWithDirection } = useNavigationValidation()
  const { triggerSave } = useFormSaveForLater()

  const handleMenuToggle = () => {
    setMobileMenu(!mobileMenu)
  }

  const getActiveRouteName = () => {
    const route = ROUTES.PERSONAL.ROOT
    const currentRoute = location.pathname
    const routeName = Object.values(ROUTES.PERSONAL).find((route) => route.path === currentRoute)

    return routeName ? routeName.name : route.name
  }

  const activeRouteName = getActiveRouteName()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    handleScroll()

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const [open, setOpen] = useState(false)

  const handleSaveApplicationClick = async () => {
    // Trigger save callback of the current step
    triggerSave()
    
    // Get current URL parameters
    const currentParams = new URLSearchParams(window.location.search || '')
    // Set or update the formid parameter
    currentParams.set('formid', state.formId || '' )
    
    // Show the save URL modal
    setSaveUrl(window.location.origin + '/personal?' + currentParams.toString())
    setOpen(true)
  }

  const handleNavigation = (path: string) => {
    if (validateNavigationWithDirection(location.pathname, path)) {
      navigate(path)
    } else {
      setMobileMenu(false)
      return false
    }
    return true
  }

  const handleYourDetailsClick = () => handleNavigation(ROUTES.PERSONAL.YOUR_DETAILS.path)
  const handleAboutYouClick = () => handleNavigation(ROUTES.PERSONAL.ABOUT_YOU.path)
  const handleIncomeClick = () => handleNavigation(ROUTES.PERSONAL.INCOME.path)
  const handleFinancialPositionClick = () => handleNavigation(ROUTES.PERSONAL.FINANCIAL_POSITION.path)
  const handleDocUploadClick = () => handleNavigation(ROUTES.PERSONAL.DOC_UPLOAD.path)

  return (
    <>
      <header className='text-sm group' data-mobile-menu={mobileMenu || null} data-is-scrolled={isScrolled || null}>
        <div className='fixed top-0 z-20 left-0 w-full px-12 max-xl:px-0 max-xl:shadow-xl transition-all duration-300 group-data-[is-scrolled]:px-0 max-xl:group-data-[mobile-menu]:h-screen'>
          <div className='bg-white overflow-hidden transition-all duration-300 h-[72px] max-lg:group-data-[mobile-menu]:h-screen max-xl:group-data-[mobile-menu]:h-[200px] rounded-full px-4 flex items-start justify-between max-xl:flex-wrap translate-y-4 max-xl:translate-y-0 max-xl:rounded-none group-data-[is-scrolled]:rounded-none group-data-[is-scrolled]:translate-y-0'>

            <PersistedLink to='/' className='w-[120px] md:w-[134px] mr-1 md:mx-6 flex items-center h-[72px]'>
              <img
                className='w-full'
                src={dealerContext?.themeLogo || '/brand/udc/site-logo.png'}
                alt='Logo'
              />
            </PersistedLink>

            <div className='xl:hidden h-[72px] flex justify-center grow max-sm:max-w-[94px]'>
              <span className={navLinkIndicatorClasses}>
                {activeRouteName}
              </span>
            </div>
            <nav className='grow flex flex-col lg:flex-row justify-evenly gap-7 h-[72px] max-lg:justify-start max-xl:h-auto max-xl:w-full max-xl:order-last max-xl:top-20 max-xl:left-0 max-xl:right-0 max-xl:opacity-0 max-xl:group-data-[mobile-menu]:delay-300 max-xl:group-data-[mobile-menu]:opacity-100 max-xl:pt-4 max-lg:pt-10 transition max-lg:group-data-[mobile-menu]:duration-500 max-lg:overflow-auto max-lg:h-[calc(100dvh-72px)] max-lg:-mx-4'>
              <PersistedLink 
                className={navLinkClasses} 
                to={ROUTES.PERSONAL.YOUR_DETAILS.path} 
                isNavLink
                onClick={handleYourDetailsClick}
              >
                {ROUTES.PERSONAL.YOUR_DETAILS.name}
              </PersistedLink>
              <PersistedLink 
                className={navLinkClasses} 
                to={ROUTES.PERSONAL.ABOUT_YOU.path} 
                isNavLink
                onClick={handleAboutYouClick}
              >
                {ROUTES.PERSONAL.ABOUT_YOU.name}
              </PersistedLink>
              <PersistedLink 
                className={navLinkClasses} 
                to={ROUTES.PERSONAL.INCOME.path} 
                isNavLink
                onClick={handleIncomeClick}
              >
                {ROUTES.PERSONAL.INCOME.name}
              </PersistedLink>
              <PersistedLink 
                className={navLinkClasses} 
                to={ROUTES.PERSONAL.FINANCIAL_POSITION.path} 
                isNavLink
                onClick={handleFinancialPositionClick}
              >
                {ROUTES.PERSONAL.FINANCIAL_POSITION.name}
              </PersistedLink>
              <PersistedLink 
                className={navLinkClasses} 
                to={ROUTES.PERSONAL.DOC_UPLOAD.path} 
                isNavLink
                onClick={handleDocUploadClick}
              >
                {ROUTES.PERSONAL.DOC_UPLOAD.name}
              </PersistedLink>
            </nav>
            <div className='max-xl:hidden flex items-center gap-4 text-[var(--brand-1)] pl-5'>
              {hasPurchaseItems && (
                <>
                  {(() => {
                    const dealerIcon = dealerContext?.dealerThemeIcon?.buyingOptions?.find(item => item.value === purchaseItems.itemType)?.icon
                    const defaultIcon = purchasePersonalItems.find(item => item.value === purchaseItems.itemType)?.icon
                    const iconSrc = dealerIcon ? dealerIcon : defaultIcon
                    
                    return iconSrc ? (
                      <ReactSVG 
                        className='Header-svg h-[72px] w-[72px] flex items-center' 
                        src={iconSrc} 
                      />
                    ) : null
                  })()}
                  <span className='text-[28px] font-[var(--font-weight-bold)] pl-1'>${purchaseItems.purchasePrice}</span>
                  <div className='flex flex-col max-w-[130px] text-xs leading-[1.25]'>
                    <span>{purchaseItems.repaymentFrequency} payments</span>
                    <span>{purchaseItems.loanTerm.charAt(0) * 12} months term</span>
                  </div>
                  <Button 
                    className='header-save-button [&.header-save-button]:rounded-full' 
                    icon='none' 
                    onClick={handleSaveApplicationClick}

                  >
                    Save application
                  </Button>
                </>
              )}
            </div>
            <div className='w-[120px] md:w-[134px] h-[72px] flex items-center justify-end gap-2 ml-1 xl:hidden'>
              {hasPurchaseItems && (
                <Button 
                  className='header-save-button [&.header-save-button]:rounded-full whitespace-nowrap max-lg:hidden' 
                  icon='none' 
                  onClick={handleSaveApplicationClick}
                >
                  Save application
                </Button>
              )}
              <button
                className='cursor-pointer transition duration-300 flex items-center justify-center rounded-full p-2 hover:bg-[var(--theme-bg)] hover:text-[var(--brand-1)] '
                onClick={handleMenuToggle}
              >
                <Bars3Icon className='w-6 h-6 relative z-10' aria-hidden='true' onClick={handleMenuToggle} />
              </button>
            </div>
          </div>
        </div>
      </header>
      <div className='h-20'></div>
      {hasPurchaseItems && (
        <div className='Header-item max-xl:px-3 max-xl:py-3 lg:hidden fixed bottom-0 left-auto right-0 z-10'>
          <Button 
            className='header-save-button [&.header-save-button]:rounded-full' 
            icon='none' 
            onClick={handleSaveApplicationClick}
          >
            Save application
          </Button>
        </div>
      )}
      <SaveApplicationModal open={open} setOpen={setOpen} saveUrl={saveUrl} />
    </>
  )
}

export { Header }
